import React, {CSSProperties, MouseEventHandler, ReactElement, SyntheticEvent, UIEventHandler, useCallback, useEffect, useRef, useState} from "react";
import "./ContentBlock.less";
import IconButton from "../IconButton/IconButton";
import Icons from "../IconButton/Icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import getParents from "../../lib/picos/getParents";
import ErrorBoundaryO from "../../ErrorBoundary/ErrorBoundaryO";
import type {IconProp} from "@fortawesome/fontawesome-svg-core";
import useIsMounted from "../../hooks/useIsMounted";
import {SizeProps} from "./ContentBlockContainer";
import useComponentCache from "../../hooks/useComponentCache";

/**
 * Wraps a component inside a ContentBlock, to be displayed by a ContentBlockContainer.
 */

// Height of the title bar.
const contentBlockTitleHeight = "2.1em";

// Only want to show footer if not expanded.
const expandedContentBlockContentHeight = () => `calc(100% - ${contentBlockTitleHeight} - 4px)`;

type ContentBlockState = {
    currentWidth: number;
    hasAncestors: boolean;
    hasDescendants: boolean;
    hasExpandedDescendants: boolean;
    inTesting: number;
    isExpanded: boolean | undefined;
    isInView: boolean;
    offsetHeight: number;
    scrollLeft: number;
    scrollTop: number;
}

function ContentBlock(props: ContentBlockProps) {
    const isMounted = useIsMounted();

    // Namespaced to avoid collisions with arbitrary same-named cache keys
    // elsewhere in the app. Two `<ContentBlock title="X">` instances mounted
    // at once will still share state — that has always been the case and is
    // outside the scope of this fix.
    const {cacheValue, cacheSave} = useComponentCache<ContentBlockState>(`content-block-${props.title}`);

    const [currentWidth, setCurrentWidth] = useState<number>(cacheValue('currentWidth', 0));
    const [hasAncestors, setHasAncestors] = useState<boolean>(cacheValue('hasAncestors', false));
    const [hasDescendants, setHasDescendants] = useState<boolean>(cacheValue('hasDescendants', false));
    const [hasExpandedDescendants, setHasExpandedDescendants] = useState<boolean>(cacheValue('hasExpandedDescendants', false));
    const [inTesting, setInTesting] = useState<number>(cacheValue('inTesting', 0));
    const [isExpanded, setIsExpanded] = useState<boolean | undefined>(cacheValue('isExpanded', props.isExpanded));
    const [isInView, setIsInView] = useState<boolean>(cacheValue('isInView', false));
    const [offsetHeight, setOffsetHeight] = useState<number>(cacheValue('offsetHeight', 0));
    const [scrollLeft, setScrollLeft] = useState<number>(cacheValue('scrollLeft', 0));
    const [scrollTop, setScrollTop] = useState<number>(cacheValue('scrollTop', 0));

    // A reference to this ContentBlock
    const selfRef = useRef<HTMLDivElement>(null);

    // A reference to content-block__content
    const contentRef = useRef<HTMLDivElement>(null);

    // Check for expanded state changes
    useEffect(() => {
        if (props.isExpanded !== isExpanded) setIsExpanded(props.isExpanded);
    }, [isExpanded, props.isExpanded, setIsExpanded]);

    // Restore scroll position on mount / when scrollTop changes.
    // Use property assignment instead of scrollTo() method — React Compiler
    // wraps useRef values in a Proxy, and native Element.scrollTo() throws
    // "Illegal invocation" when `this` is a Proxy instead of a real Element.
    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTop = scrollTop ?? 0;
        }
    }, [contentRef, scrollTop]);

    // Save cache
    useEffect(() => {
        return () => {
            cacheSave({currentWidth, hasAncestors, hasDescendants, hasExpandedDescendants, inTesting, isExpanded, isInView, offsetHeight, scrollLeft, scrollTop});
        }
    }, [cacheSave, currentWidth, hasAncestors, hasDescendants, hasExpandedDescendants, inTesting, isExpanded, isInView, offsetHeight, scrollLeft, scrollTop]);

    // Handle changes in height
    const updateDimensions = useCallback(() => {
        if (selfRef?.current) {
            const _offsetHeight = selfRef.current?.offsetHeight;

            if (!!_offsetHeight && _offsetHeight !== offsetHeight) {
                setOffsetHeight(_offsetHeight);
            }
        }
    }, [offsetHeight, selfRef, setOffsetHeight]);

    useEffect(() => {
        const container = document.getElementById(`cb_${props.title}`);

        let observer: IntersectionObserver | undefined;

        // Observer to see if this is currently in view. We lazy load contents.
        if (container) {
            const options = {
                root: null,
                rootMargin: "0px",
                threshold: 0,
                target: container,
            };

            const callback: IntersectionObserverCallback = (entries, _observer) => {
                // Once it has been shown, we keep it loaded
                const isInView = entries[0].isIntersecting;
                if (isInView) setIsInView(isInView);
            };

            observer = new IntersectionObserver(callback, options);
            observer.observe(container);
        }

        return () => {
            // Remove visibility observer
            if (isMounted() && observer && container) {
                observer.unobserve(container);
            }
        }
    }, [isMounted, props.title, setIsInView]);

    useEffect(() => {
        window.addEventListener("resize", updateDimensions);

        // Force initial update
        updateDimensions();

        return () => {
            // Remove resize observer
            window.removeEventListener("resize", updateDimensions);
        }
    }, [currentWidth, hasAncestors, hasDescendants, hasExpandedDescendants, inTesting, isExpanded, isInView, isMounted, offsetHeight, scrollLeft, scrollTop, selfRef, setIsInView, updateDimensions]);

    /**
     * Called after a render
     *
     * @param _pp - previous props
     * @param ps - previous state
     */
    useEffect(() => {
        const thisNode = selfRef?.current;
        if (!thisNode) {
            // Don't log error on initial render, only if ref should exist
            if (isMounted()) {
                console.warn("ContentBlock: selfRef not yet attached");
            }
            return;
        }

        // Structure is content-block-container > content-block-container__content > content-block
        const _hasDescendants = thisNode.querySelectorAll(
            ".content-block-container"
        ).length > 0;
        const _inTesting = thisNode.querySelectorAll(".TEST").length;
        const _hasExpandedDescendants = thisNode.querySelectorAll(
            ".content-block--expanded"
        ).length > 0;
        const _hasAncestors = getParents(
            thisNode,
            ".content-block-container"
        ).length > 0;
        const _currentWidth = selfRef.current.offsetWidth;

        if (_hasAncestors !== hasAncestors) setHasAncestors(_hasAncestors);
        if (_hasDescendants !== hasDescendants) setHasDescendants(_hasDescendants);
        if (_hasExpandedDescendants !== hasExpandedDescendants) setHasExpandedDescendants(_hasExpandedDescendants);
        if (isExpanded !== props.isExpanded) setIsExpanded(props.isExpanded);
        if (_inTesting!== inTesting) setInTesting(_inTesting);
        if (_currentWidth!== currentWidth) setCurrentWidth(_currentWidth);

        if (
            _hasAncestors !== hasAncestors ||
            _hasDescendants !== hasDescendants ||
            _hasExpandedDescendants !== hasExpandedDescendants ||
            isExpanded !== props.isExpanded ||
            _inTesting !== inTesting ||
            _currentWidth !== currentWidth
        ) updateDimensions();


    }, [currentWidth, hasAncestors, hasDescendants, hasExpandedDescendants, inTesting, isExpanded, isMounted, props.isExpanded, selfRef, setCurrentWidth, setHasAncestors, setHasDescendants, setHasExpandedDescendants, setInTesting, setIsExpanded, updateDimensions]);

    const onScrollCapture: UIEventHandler<HTMLDivElement> = useCallback<UIEventHandler<HTMLDivElement>>((_e) => {
        if (contentRef.current) {
            const _scrollTop = contentRef.current.scrollTop;
            const _scrollLeft = contentRef.current.scrollLeft;

            if (_scrollLeft && _scrollLeft !== scrollLeft)
                setScrollLeft(_scrollLeft);
            if (_scrollTop && _scrollTop !== scrollTop)
                setScrollTop(_scrollTop);
        }
    }, [contentRef, scrollLeft, scrollTop]);

    /**
     * Called from wrapped components to let the ContentBlock know their current state.
     * This is used when accepting changes.
     */
    const onChange = useCallback((value: any) => {
        // Now call the handler
        if (props.onEditCompleted)
            props.onEditCompleted(value);
    }, [props]);

    /**
     * Called when the collapse icon is clicked
     */
    const onCollapse = useCallback((e: SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();

        setIsExpanded(false);

        if (props.onCollapse) props.onCollapse(props.title);
    }, [setIsExpanded, props]);

    /**
     * Called whe the expand icon is clicked
     */
    const onExpand = useCallback((e: SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();

        setIsExpanded(true);

        if (props.onExpand) props.onExpand(props.title);
    }, [setIsExpanded, props]);

    const handleDoubleClick: MouseEventHandler<HTMLDivElement> = useCallback((e) => {
        if (props.isExpanded) onCollapse(e);
        else onExpand(e);
    }, [props.isExpanded, onCollapse, onExpand]);

    // Styles for expanded content-block
    const contentBlockStyles: CSSProperties = {};

    // Styles for unexpanded content-block__content
    const contentStyles: CSSProperties = {
        zIndex: isExpanded ? 100000000 : undefined,
        padding: hasExpandedDescendants ? 0 : "5px 5px 0 5px", // can't use margin or iOS loses its shit
        // minHeight: regularContentBlockContentHeight,
        // maxHeight: regularContentBlockContentHeight
    };

    // If there are expanded descendents, get rid of border radius on expand/collapse icon
    const expandIconStyle = hasExpandedDescendants ? {borderRadius: 0} : {};

    // Styles for the title-bar.  These can be sent through props (in which case we use them) or through a class
    const titleStyle: CSSProperties = {};
    if (props.backgroundColor)
        titleStyle.backgroundColor = props.backgroundColor;
    if (props.color) titleStyle.color = props.color;

    // A class name to use, and a flag to check!
    let expandedClass = "";

    if (isExpanded || hasExpandedDescendants) {
        // content-block--expanded is for use with descendant React components
        expandedClass = "content-block--expanded";

        // Styles for expanded content-block
        Object.assign(contentBlockStyles, {
            backfaceVisibility: "hidden",
            border: inTesting ? "3px dashed red" : "unset",
            borderRadius: "unset",
            display: "flex",        // keep flex so content fills space (was "block")
            flexDirection: "column", // explicit since position:fixed resets layout context
            left: 0,
            margin: 0,
            height: '100%',         // explicit height for child percentage resolution
            minHeight: '100%',
            maxHeight: '100%',
            overflow: "visible",
            padding: "0",
            position: "fixed",
            top: 0,
            width: "100%",
            zIndex: 1000,
        });

        // Set styles for content-block__content
        Object.assign(contentStyles, {
            // This calculation works for TaskItem and Contact
            border: inTesting ? "3px dashed orange" : "unset",
            height: expandedContentBlockContentHeight,
            minHeight: expandedContentBlockContentHeight,
            maxHeight: expandedContentBlockContentHeight,
        });
    }

    const autoScroll = props.autoScroll ? "content-block--auto-scroll" : "";

    const expansionIcon = isExpanded ? (
        <IconButton
            id={`${props.title}-collapse`}
            key="collapse"
            tabIndex={-1}
            type={Icons.COMPRESS}
            color="black"
            onClick={onCollapse}
            title="Restore content to normal size"
        />
    ) : <IconButton id={`${props.title}-expand`}
                    key="expand"
                    tabIndex={-1}
                    type={Icons.EXPAND}
                    color="black"
                    onClick={onExpand}
                    title="Maximize this content"
    />;

    const child = props.children;

    if (!child) return null;

    try {
        if (!React.Children.only(child))
            throw new Error("ContentBlock only supports one child");
    }
    catch (e) {
        console.error("ContentBlock only supports one child", child); // Log error to console
    }

    const childProps: Partial<ContentBlockProps & SizeProps> = {
        key: `CB-${props.parentKey}-${props.title}`,
        onChange,
        isExpanded,
        // gridColumns: props.gridColumns,
        // gridRows: props.gridRows,
        // gridNumber: props.gridNumber,
        containerWidth: props.containerWidth,
        useSmallDisplay: props.useSmallDisplay,
        containerHeight: offsetHeight,
        hasAncestors,
        hasDescendants,
        hasExpandedDescendants,
    }

    // This clone adds more context to the contained component.
    // Size props (containerWidth/useSmallDisplay/containerHeight) are re-applied
    // AFTER child.props so the layout-owned values from ContentBlock always win —
    // a child that doesn't explicitly pass them would otherwise lose the injected
    // value if any wrapper/spread introduced an undefined entry for those keys.
    const childrenWithProps = React.cloneElement(child as React.ReactElement<any>, {
        ...childProps,
        ...child.props as object,
        containerWidth: props.containerWidth,
        useSmallDisplay: props.useSmallDisplay,
        containerHeight: offsetHeight,
    });

    if (!isExpanded && props.overrideHeight) {
        contentBlockStyles.height = props.overrideHeight;
        // Don't set minHeight when autoScroll is enabled - it prevents the scrollbar from appearing
        if (!props.autoScroll) {
            contentStyles.minHeight = "fit-content";
        }
    }

    if (props.isHidden || props.isHiddenOverride) return null;
    else
        return (
            <ErrorBoundaryO uniqueKey={`content-block-top-${props.title}`} shouldUseErrorFrame={true}>
                <div
                    id={`cb_${props.title}`}
                    key={props.title}
                    ref={selfRef}
                    style={contentBlockStyles}
                    className={`content-block ${expandedClass}`}
                >
                    {/* Title Bar */}
                    <div
                        className={`content-block__title-bar ${
                            props.customStyles || ""
                        }`}
                        style={titleStyle}
                    >
                        {/* If an Icon is present, render it */}
                        {!props.singleViewMode && props.icon && (
                            <div className="content-block__icon">
                                <FontAwesomeIcon icon={props.icon}/>
                            </div>
                        )}

                        {/* Title for the content block, just to the right of the icon */}
                        {!props.singleViewMode && (
                            <div
                                className="content-block__title"
                                onDoubleClick={handleDoubleClick}
                            >
                                {props.displayTitle ?? props.title}
                            </div>
                        )}

                        {/* If a message is present, render it */}
                        {props.message && (
                            <div
                                className={`content-block__message ${
                                    props.singleViewMode ? "single" : ""
                                }`}
                            >
                                {props.message}
                            </div>
                        )}

                        {/* Expand/Contract icon */}
                        {!props.singleViewMode && !hasExpandedDescendants && (
                            <div
                                className={`content-block__actions`}
                                style={expandIconStyle}
                            >
                                {expansionIcon}
                            </div>
                        )}
                    </div>

                    <div
                        className={`content-block__content ${autoScroll}`}
                        style={contentStyles}
                        ref={contentRef}
                        onScrollCapture={onScrollCapture}
                    >
                        {isInView && !props.disabled && (
                            <ErrorBoundaryO uniqueKey={`content-block-${props.title}`}
                                            shouldUseErrorFrame={true}
                            >
                                {childrenWithProps}
                            </ErrorBoundaryO>
                        )}
                    </div>

                    {!(isExpanded || hasExpandedDescendants) && (
                        <div className="content-block__footer"/>
                    )}
                </div>
            </ErrorBoundaryO>
        );
}

export type ExpandCallback = (title: string) => void;
export type CollapseCallback = (title: string) => void;

// These are passed down to the child component
export type ContentBlockChildProps = {
    // Key of the parent component. This is filled in by ContentBlockContainer, so we do not need to expose this to Components.
    parentKey?: string,

    // Is this component currently hidden?
    isHidden?: boolean,

    // Current number of columns currently being used in the grid.
    // Always 1 if in tabbed mode.
    gridColumns?: number,

    // Number of rows currently being used in the grid
    gridRows?: number,

    // Number of the grid linearly (in a 3x2 grid, gridNumber 4 would be the first item in the second row)
    gridNumber?: number,

    // Callback for expanding a content block, passed the title of the block
    onExpand?: ExpandCallback,

    // Callback for the collapsing a content block, nothing passed back
    onCollapse?: CollapseCallback,

    // Current width of the child container
    containerWidth?: number,

    // Current height of the child container
    containerHeight?: number,

    // If the height of this child container is overridden, what is the fixed height?
    overrideHeight?: string,

    // Should this block be shown in small display mode?
    useSmallDisplay?: boolean,

    // Is this block in singleViewMode (tabs)?
    singleViewMode?: boolean,

    // Is this block currently expanded?
    isExpanded?: boolean,
}

export type ContentBlockProps = ContentBlockChildProps & {
    // Unique key
    key?: string;

    // Should a scrollbar be shown for content?
    autoScroll?: boolean;

    // background color
    backgroundColor?: string;

    // font color
    color?: "black" | "white";

    // From Container, height of this container block
    containerHeight?: number;

    // From Container, width of this container block
    containerWidth?: number;

    // Should the contents of this block be read-only/disabled?
    disabled?: boolean;

    // Does this block have any ancestors?
    hasAncestors?: boolean;

    // Does this block have any descendants?
    hasDescendants?: boolean;

    // Does this block have any expanded descendants?
    hasExpandedDescendants?: boolean;

    // Is there any content to show?  If no, it will remain collapsed
    hasContent?: boolean;

    // Icon for block, from IconButton
    icon?: IconProp;

    // Is this block expanded?
    isExpanded?: boolean;

    // Is this block hidden
    isHidden?: boolean;
    isHiddenOverride?: boolean;

    // Styles to apply to the title bar and icon
    customStyles?: string;

    // Optional message in the title
    message?: React.ReactElement | string;

    // The component's unique(ish) name. Used if the title prop is not a simple string
    // name?: string,

    // Change handler (at the ContentBlock level)
    onChange?: (value?: any) => void,

    // Should we override the height of this ContentBlock?
    overrideHeight?: string;

    // The title for this block
    title: string;

    // A title to display in lieu of the normal title
    displayTitle?: string | ReactElement;

    // From Container, is the small display flag set?  If so, content-block--size-small will be set
    useSmallDisplay?: boolean;

    // Callback used by ContentBlocks to pass back changes
    onEditCompleted?: (args: any) => void,

    // Callbacks for expand/collapse
    onExpand?: ExpandCallback;
    onCollapse?: CollapseCallback;

    // Is this box in single view mode?
    singleViewMode?: boolean;

    // How many columns should this span?  -1 means all.
    span?: number;

    // URL path segment for deep linking (e.g., 'invoices', 'payments-dashboard').
    // Used by ContentBlockContainer to map URL paths to tabs.
    urlKey?: string;

    // Additional props needed based on component usage
    children: React.ReactElement;
}

export default ContentBlock;
